/**
 * Resignation on a person's employment tab. People file their own;
 * Human Capital records one for someone (approved at once), decides on
 * requests and can cancel, up to six months after the effective day.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, get, post } from '../lib/api.ts';
import { date } from '../lib/format.ts';
import { useMe } from '../lib/session.ts';
import { Button, Callout, Chip, Dialog, Empty, Field, Panel, Switch, useToast } from './ui.tsx';

export interface ResignationRow {
  id: string;
  effectiveOn: string;
  submittedOn: string | null;
  reason: string | null;
  notes: string | null;
  professional: boolean;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  processedAt: string | null;
}

const STATUS = { pending: { tone: 'wait', label: 'Waiting for Human Capital' }, approved: { tone: 'ok', label: 'Approved' }, rejected: { tone: 'bad', label: 'Rejected' }, cancelled: { tone: 'plain', label: 'Cancelled' } } as const;
const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

export function ResignationPanel({ employeeId, self, active }: { employeeId: string; self: boolean; active: boolean }) {
  const me = useMe().data!;
  const qc = useQueryClient();
  const toast = useToast();
  const manage = me.access.resignationManage && !self;
  const q = useQuery({ queryKey: ['resignations', employeeId], queryFn: () => get<ResignationRow[]>(`/api/people/${employeeId}/resignation`) });
  const [filing, setFiling] = useState(false);
  const [cancelling, setCancelling] = useState<ResignationRow | null>(null);
  const open = q.data?.find((r) => r.status === 'pending' || r.status === 'approved');
  const past = q.data?.filter((r) => r !== open).slice(0, 3) ?? [];
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['resignations', employeeId] });
    void qc.invalidateQueries({ queryKey: ['person', employeeId] });
    void qc.invalidateQueries({ queryKey: ['resignations-pending'] });
  };
  const decide = useMutation({
    mutationFn: (v: { id: string; decision: 'approved' | 'rejected' }) => post(`/api/people/resignations/${v.id}/decide`, { decision: v.decision }),
    onSuccess: (_r, v) => {
      refresh();
      toast({ title: v.decision === 'approved' ? 'Resignation approved' : 'Resignation rejected' });
    },
    onError: (e) => toast({ title: errText(e), tone: 'error' }),
  });

  return (
    <Panel
      eyebrow="Leaving"
      title="Resignation"
      action={!open && active && (self || manage) ? <Button size="sm" variant="ghost" onClick={() => setFiling(true)}>{self ? 'Resign' : 'Record resignation'}</Button> : undefined}
    >
      {open ? (
        <div className="stack">
          <dl className="dl">
            <dt>Status</dt>
            <dd><Chip tone={STATUS[open.status].tone}>{open.processedAt ? 'Has left' : STATUS[open.status].label}</Chip></dd>
            <dt>Last day</dt>
            <dd>{date(open.effectiveOn, { year: true })}</dd>
            <dt>Filed</dt>
            <dd>{date(open.submittedOn, { year: true })}</dd>
            <dt>Final payslip</dt>
            <dd>{open.professional ? 'Yes (professional resignation)' : 'No'}</dd>
            {open.reason ? (
              <>
                <dt>Reason</dt>
                <dd className="muted">“{open.reason}”</dd>
              </>
            ) : null}
          </dl>
          <div className="row row--wrap" style={{ gap: 8 }}>
            {manage && open.status === 'pending' ? (
              <>
                <Button size="sm" variant="primary" loading={decide.isPending && decide.variables?.decision === 'approved'} onClick={() => decide.mutate({ id: open.id, decision: 'approved' })}>Approve</Button>
                <Button size="sm" loading={decide.isPending && decide.variables?.decision === 'rejected'} onClick={() => decide.mutate({ id: open.id, decision: 'rejected' })}>Reject</Button>
              </>
            ) : null}
            {manage || (self && open.status === 'pending') ? <Button size="sm" variant="ghost" onClick={() => setCancelling(open)}>{self ? 'Withdraw' : 'Cancel resignation'}</Button> : null}
          </div>
        </div>
      ) : !past.length ? (
        <Empty title="No resignation on file." />
      ) : null}
      {past.length ? (
        <ul className="list-plain" style={{ marginTop: open ? 'var(--s-5)' : 0, fontSize: 'var(--step--1)' }}>
          {past.map((r) => (
            <li key={r.id} className="row row--between" style={{ padding: '8px 0', borderTop: '1px solid var(--line-1)' }}>
              <span>Last day {date(r.effectiveOn, { year: true })}{r.notes ? <span className="faint"> · {r.notes}</span> : null}</span>
              <Chip tone={STATUS[r.status].tone}>{r.status}</Chip>
            </li>
          ))}
        </ul>
      ) : null}
      <FileResignation open={filing} onClose={() => setFiling(false)} employeeId={employeeId} self={self} onDone={refresh} />
      <CancelResignation row={cancelling} onClose={() => setCancelling(null)} self={self} onDone={refresh} />
    </Panel>
  );
}

function FileResignation({ open, onClose, employeeId, self, onDone }: { open: boolean; onClose: () => void; employeeId: string; self: boolean; onDone: () => void }) {
  const toast = useToast();
  const [f, setF] = useState({ effectiveOn: '', reason: '', professional: true });
  const m = useMutation({
    mutationFn: () => post<ResignationRow>(`/api/people/${employeeId}/resignation`, { effectiveOn: f.effectiveOn, reason: f.reason.trim() || undefined, professional: f.professional }),
    onSuccess: (r) => {
      onClose();
      onDone();
      toast({ title: r.status === 'approved' ? 'Resignation recorded' : 'Resignation sent to Human Capital', body: r.processedAt ? 'The last day has passed, so they have been signed out everywhere.' : undefined });
    },
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow={self ? 'Your resignation' : 'Human Capital'}
      title={self ? <>Hand in your <em className="serif-italic">notice</em></> : <>Record a <em className="serif-italic">resignation</em></>}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!f.effectiveOn} loading={m.isPending} onClick={() => m.mutate()}>{self ? 'Send' : 'Record'}</Button></>}
    >
      <div className="stack">
        <p className="muted" style={{ fontSize: 'var(--step--1)' }}>
          {self ? 'Human Capital decides on it. You can withdraw it until then.' : 'A resignation you record is approved at once. On the last day they are marked as left, signed out of every app and taken off the Fingerspot machines overnight.'}
        </p>
        <Field label="Last working day">{(p) => <input {...p} type="date" className="input" value={f.effectiveOn} onChange={(e) => setF({ ...f, effectiveOn: e.target.value })} />}</Field>
        <Field label="Reason" hint="Optional">{(p) => <textarea {...p} className="textarea" rows={3} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />}</Field>
        {self ? null : <Switch checked={f.professional} onChange={(v) => setF({ ...f, professional: v })} label="Professional resignation (receives a final payslip)" showLabel />}
        {m.error ? <Callout tone="bad">{errText(m.error)}</Callout> : null}
      </div>
    </Dialog>
  );
}

function CancelResignation({ row, onClose, self, onDone }: { row: ResignationRow | null; onClose: () => void; self: boolean; onDone: () => void }) {
  const toast = useToast();
  const [note, setNote] = useState('');
  const m = useMutation({
    mutationFn: () => post<{ reinstated: boolean }>(`/api/people/resignations/${row!.id}/cancel`, { note }),
    onSuccess: (r) => {
      onClose();
      setNote('');
      onDone();
      toast({ title: r.reinstated ? 'Cancelled; they are active again' : 'Resignation cancelled', body: r.reinstated ? 'They can sign in again. Put them back on the Fingerspot machines from Devices.' : undefined });
    },
  });
  return (
    <Dialog
      open={!!row}
      onClose={onClose}
      title={self ? 'Withdraw your resignation?' : 'Cancel this resignation?'}
      footer={<><Button variant="ghost" onClick={onClose}>Keep it</Button><Button variant="primary" disabled={note.trim().length < 3} loading={m.isPending} onClick={() => m.mutate()}>{self ? 'Withdraw' : 'Cancel resignation'}</Button></>}
    >
      <div className="stack">
        {row?.processedAt ? <Callout tone="info">They have already left. Cancelling makes them active again and lets them sign in.</Callout> : null}
        <Field label="Why">{(p) => <textarea {...p} className="textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
        {m.error ? <Callout tone="bad">{errText(m.error)}</Callout> : null}
      </div>
    </Dialog>
  );
}
